import type { ReactNode } from 'react'
import type { ColumnFiltersState, PaginationState } from '@tanstack/react-table'
import type { TableHeaderIconName } from './components/TableHeaderIcon'

export type { TableHeaderIconName }
export type { RowAccent } from './rowAccent'

/** Filter chip types supported in the table toolbar. */
export type ColumnFilterType =
  | 'checkbox'
  | 'select'
  | 'range'
  | 'daterange'
  | 'entity'

export type EntityFilterKind = 'user' | 'organization' | 'product'

export interface EntityFilterOption {
  id: string
  label: string
  /** Optional secondary line (email, slug, serial, …). */
  subtitle?: string
}

export interface EntityFilterPageResult {
  data: EntityFilterOption[]
  total: number
}

export interface EntityFilterConfig {
  kind: EntityFilterKind
  /** Fetch a page of options for the picker. */
  fetchPage: (args: {
    search: string
    page: number
    limit: number
  }) => Promise<EntityFilterPageResult>
  /**
   * Resolve labels for selected IDs (chip display after reload).
   * Optional — falls back to raw IDs when omitted.
   */
  resolveLabels?: (ids: string[]) => Promise<Record<string, string>>
  searchPlaceholder?: string
  emptyMessage?: string
  pageSize?: number
}

export interface ColumnFilterOption {
  value: string
  label: string
  /** Optional hover tooltip (e.g. underlying telemetry key). */
  title?: string
  /** Inclusive min for range filters. */
  min?: number
  /** Inclusive max for range filters. */
  max?: number
}

/**
 * Declarative column + filter metadata (replaces archived entityDefinitions + ColumnDef meta).
 */
export interface ColumnDefinition<TRow> {
  id: string
  /** Human-readable name (Columns/Filters menus, header text when no headerLabelIcon). */
  label?: string
  /**
   * @deprecated Prefer `label`. Kept for older column defs; used when `label` is omitted.
   */
  header?: string
  /** When set, the table header shows this icon instead of label text. */
  headerLabelIcon?: TableHeaderIconName
  width?: number
  /** Minimum width when using fillRemaining. Defaults to width ?? 150. */
  minWidth?: number
  /**
   * @deprecated Fill behavior is applied to the rightmost visible column automatically.
   * Use width / minWidth to control size when that column is not rightmost.
   */
  fillRemaining?: boolean
  sortable?: boolean
  /** API / server sort field when different from id. */
  sortKey?: string
  /** Client-side sort only (ignored when using QueryTable). */
  sort?: (a: TRow, b: TRow) => number
  filterType?: ColumnFilterType
  /** API / server filter field when different from column id (e.g. phone_verified). */
  filterKey?: string
  /** Toolbar chip label; defaults to label. */
  filterLabel?: string
  filterOptions?:
    | ColumnFilterOption[]
    | ((rows: TRow[]) => ColumnFilterOption[])
  filterAccessor?: (row: TRow) => string | string[]
  /** Async entity picker config when filterType is `entity`. */
  entityFilter?: EntityFilterConfig
  /** When false, chip hidden unless enabled in Filters config. Default true if filterType set. */
  showFilterByDefault?: boolean
  render: (row: TRow) => ReactNode
  defaultHidden?: boolean
  /** When false, column cannot be hidden via Columns menu. Default true. */
  enableHiding?: boolean
  /** Always render this column first regardless of saved column order. */
  pinnedLeft?: boolean
}

/** @deprecated Alias for ColumnDefinition */
export type Column<TRow> = ColumnDefinition<TRow>

export function getColumnLabel<TRow>(col: ColumnDefinition<TRow>): string {
  return col.label ?? col.header ?? col.id
}

export interface TablePreferencesV1 {
  version: 1
  pagination: PaginationState
  columnFilters: ColumnFiltersState
  sort?: { sort_by: string; order: 'asc' | 'desc' }
  columnVisibility: Record<string, boolean>
  columnOrder: string[]
  /** Pixel widths per column id (TanStack columnSizing). */
  columnSizing?: Record<string, number>
  /** Column ids whose filter chips appear in the toolbar. */
  visibleFilterIds: string[]
  /** Persisted search text (server / QueryTable mode). */
  search?: string
}

export const TABLE_PREFERENCES_VERSION = 1 as const

export interface TableTab {
  value: string
  label: string
  count: number
}

export interface SelectionAction {
  icon: ReactNode
  label: string
  /** `names` is provided when the caller knows the entity label(s) — use it to personalise confirm dialogs for single-item actions. */
  onClick: (ids: string[], names?: string[]) => void
  danger?: boolean
  disabled?: boolean
  disabledTooltip?: string
}

/**
 * Declarative bundle for an entity list table (columns + row chrome + bulk actions).
 * Used by app pages; rendered via {@link QueryTable} / {@link Table}.
 */
export interface EntityTableDefinition<TRow extends { id: string }> {
  columns: ColumnDefinition<TRow>[]
  /**
   * Left-edge row accent key — must match {@link RowAccent} in `rowAccent.ts`
   * (e.g. fleet `online`, users `verified` / `unverified` / `deleted`).
   */
  getRowAccent?: (row: TRow) => string | undefined
  selectionActions?: SelectionAction[]
}
